import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CaretLeft, CaretRight, Plus, MagnifyingGlass } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useLanguage } from '../context/LanguageContext';
import { API, authCfg, pad, CleanerSelect, PdfPrintButtons, useRowSelection, SelectionBar, JobFilterChecks, NeedsCheckTag } from './CleaningScheduleShared';
import { CleaningReports } from './CleaningReports';
import { CleaningAnnual } from './CleaningAnnual';

const MONTHS_EL = ['Ιανουάριος', 'Φεβρουάριος', 'Μάρτιος', 'Απρίλιος', 'Μάιος', 'Ιούνιος', 'Ιούλιος', 'Αύγουστος', 'Σεπτέμβριος', 'Οκτώβριος', 'Νοέμβριος', 'Δεκέμβριος'];
const MONTHS_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const COL_TYPE = { roof: 'roof_terrace', windows: 'windows' };
const JOB_TYPE = { roof: 'Roof', windows: 'Windows' };
const VIEWS = [['schedule', 'Cleaning Schedule'], ['cleaner', 'Detailed Cleaner Report'], ['roof', 'Roof Cleaning'], ['windows', 'Window Cleaning']];

const Item = ({ it }) => (
  <span className="whitespace-nowrap inline-flex items-baseline gap-1" data-testid="schedule-item">
    <span className="font-mono text-[12px] font-semibold text-zinc-900">{it.date.slice(8, 10)}/{it.date.slice(5, 7)}</span>
    <span className="text-[10px] text-zinc-500">{it.cleaner || '—'}</span>
  </span>
);

const Cell = ({ items, onAdd, testId }) => (
  <td className="group relative px-2 py-1 border-l border-zinc-200 align-middle whitespace-nowrap" data-testid={testId}>
    <div className="flex flex-nowrap items-baseline gap-x-2.5 pr-4">
      {items.map((it, i) => <Item key={`${it.id}-${it.cleaner}-${i}`} it={it} />)}
    </div>
    <button onClick={onAdd} className="absolute right-0.5 top-1/2 -translate-y-1/2 p-0.5 text-zinc-300 opacity-60 sm:opacity-0 group-hover:opacity-100 hover:text-zinc-900 transition-opacity" title="+" data-testid={`${testId}-add`}><Plus size={11} weight="bold" /></button>
  </td>
);

const AddDialog = ({ target, cleaners, onClose, onSaved, el }) => {
  const [f, setF] = useState({ date: '', cleaner_id: '', amount: '', hours: '' });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (target) setF({ date: target.minDate, cleaner_id: '', amount: '', hours: '' }); }, [target]);
  if (!target) return null;
  const isBuilding = target.row.kind === 'building';
  const save = async () => {
    if (!f.date || !f.cleaner_id) { toast.error(el ? 'Συμπληρώστε ημερομηνία και καθαρίστρια' : 'Date and cleaner are required'); return; }
    const cl = cleaners.find((c) => c.cleaner_id === f.cleaner_id);
    setSaving(true);
    try {
      if (isBuilding) {
        await axios.post(`${API}/cleaning-history`, {
          building_id: target.row.ref_id, date: f.date, cleaner_id: f.cleaner_id, employee_name: cl?.name || '',
          cleaning_type: COL_TYPE[target.col] || 'general_cleaning',
          cleaner_payment: f.amount === '' ? null : parseFloat(f.amount),
        }, authCfg());
      } else {
        const c = { cleaner_id: f.cleaner_id, hours_worked: f.hours === '' ? 0 : parseFloat(f.hours) };
        if (f.amount !== '') c.cleaner_earned = parseFloat(f.amount);
        await axios.post(`${API}/cleaning/jobs`, { client_id: target.row.ref_id, date: f.date, cleaners: [c], notes: '', job_type: JOB_TYPE[target.col] || '' }, authCfg());
      }
      toast.success(el ? 'Η εργασία καταχωρίστηκε' : 'Job saved');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-md" aria-describedby={undefined} data-testid="schedule-add-dialog">
        <DialogHeader><DialogTitle className="text-base">{target.row.name} — {target.label}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <label className="block text-xs text-zinc-500">{el ? 'Ημερομηνία' : 'Date'}
            <Input type="date" min={target.minDate} max={target.maxDate} value={f.date} onChange={set('date')} className="rounded-none mt-1" data-testid="schedule-add-date" /></label>
          <label className="block text-xs text-zinc-500">{el ? 'Καθαρίστρια' : 'Cleaner'}
            <select value={f.cleaner_id} onChange={set('cleaner_id')} className="w-full h-9 border border-zinc-300 px-2 mt-1 text-sm" data-testid="schedule-add-cleaner">
              <option value="">—</option>
              {cleaners.map((c) => <option key={c.cleaner_id} value={c.cleaner_id}>{c.name}</option>)}
            </select></label>
          {!isBuilding && (
            <label className="block text-xs text-zinc-500">{el ? 'Ώρες' : 'Hours'}
              <Input type="number" step="0.25" value={f.hours} onChange={set('hours')} className="rounded-none mt-1" data-testid="schedule-add-hours" /></label>
          )}
          <label className="block text-xs text-zinc-500">{el ? 'Κόστος καθαρίστριας (€)' : 'Cleaner cost (€)'}
            <Input type="number" step="0.01" value={f.amount} onChange={set('amount')} className="rounded-none mt-1" data-testid="schedule-add-amount" /></label>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose} data-testid="schedule-add-cancel">{el ? 'Ακύρωση' : 'Cancel'}</Button>
          <Button className="rounded-none bg-zinc-950" disabled={saving} onClick={save} data-testid="schedule-add-save">{el ? 'Αποθήκευση' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const MonthNav = ({ el, year, month, setYM }) => {
  const shift = (d) => { const m = month + d; setYM(m < 1 ? [year - 1, 12] : m > 12 ? [year + 1, 1] : [year, m]); };
  return (
    <div className="flex items-center gap-1">
      <button onClick={() => shift(-1)} className="h-8 w-8 border border-zinc-300 bg-white flex items-center justify-center hover:bg-zinc-50" data-testid="schedule-prev-month"><CaretLeft size={14} /></button>
      <select value={month} onChange={(e) => setYM([year, Number(e.target.value)])} className="h-8 border border-zinc-300 bg-white px-2 text-sm" data-testid="schedule-month-select">
        {(el ? MONTHS_EL : MONTHS_EN).map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
      </select>
      <Input type="number" value={year} onChange={(e) => setYM([Number(e.target.value) || year, month])} className="h-8 w-20 rounded-none text-sm" data-testid="schedule-year-input" />
      <button onClick={() => shift(1)} className="h-8 w-8 border border-zinc-300 bg-white flex items-center justify-center hover:bg-zinc-50" data-testid="schedule-next-month"><CaretRight size={14} /></button>
    </div>
  );
};

const ScheduleRow = ({ r, no, selected, toggle, weeks, openAdd }) => (
  <tr className="border-t border-zinc-200 odd:bg-white even:bg-zinc-50/70 hover:bg-amber-50/40" data-testid={`schedule-row-${r.row_key}`}>
    <td className="px-1 py-1 text-center"><input type="checkbox" checked={selected} onChange={() => toggle(r.row_key)} className="accent-zinc-900" data-testid={`schedule-select-${r.row_key}`} /></td>
    <td className="px-1.5 py-1 text-right font-mono text-[11px] text-zinc-500" data-testid={`schedule-no-${r.row_key}`}>{no}</td>
    <td className="px-2 py-1 font-semibold text-zinc-900 whitespace-nowrap" title={r.name}>
      <span className="inline-block align-middle max-w-[120px] sm:max-w-[240px] truncate">{r.name}</span>
      {!r.has_entries && <NeedsCheckTag testId={`schedule-needs-check-${r.row_key}`} />}
    </td>
    {r.weeks.map((cell, i) => <Cell key={i} items={cell} onAdd={() => openAdd(r, 'week', weeks[i])} testId={`schedule-cell-${r.row_key}-w${i + 1}`} />)}
    <Cell items={r.roof} onAdd={() => openAdd(r, 'roof')} testId={`schedule-cell-${r.row_key}-roof`} />
    <Cell items={r.windows} onAdd={() => openAdd(r, 'windows')} testId={`schedule-cell-${r.row_key}-windows`} />
  </tr>
);

const ScheduleView = ({ el, cleaners }) => {
  const now = new Date();
  const [[year, month], setYM] = useState([now.getFullYear(), now.getMonth() + 1]);
  const [data, setData] = useState(null);
  const [cleanerId, setCleanerId] = useState('');
  const [search, setSearch] = useState('');
  const [addTarget, setAddTarget] = useState(null);
  const sel = useRowSelection(data?.rows, search);

  const load = useCallback(async () => {
    try { setData((await axios.get(`${API}/cleaning/schedule?year=${year}&month=${month}&cleaner_id=${cleanerId}`, authCfg())).data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  }, [year, month, cleanerId]);
  useEffect(() => { setData(null); load(); }, [load]);

  const { rows } = sel;
  const weeks = data?.weeks || [];
  const ym = `${year}-${pad(month)}`;
  const lastDay = weeks.length ? weeks[weeks.length - 1].end : 28;
  const openAdd = (row, col, w) => setAddTarget({
    row, col, label: w ? `Week ${w.index}` : (col === 'roof' ? 'Roof' : 'Windows'),
    minDate: `${ym}-${pad(w ? w.start : 1)}`, maxDate: `${ym}-${pad(w ? w.end : lastDay)}`,
  });
  const pdfParams = (cid) => () => sel.printParams({ year, month, cleaner_id: cid, lang: el ? 'el' : 'en' });

  return (
    <div data-testid="schedule-view">
      <div className="flex flex-wrap items-center gap-2 mb-2" data-testid="schedule-toolbar">
        <MonthNav el={el} year={year} month={month} setYM={setYM} />
        <CleanerSelect cleaners={cleaners} value={cleanerId} onChange={setCleanerId} el={el} testId="schedule-cleaner-select" />
        <div className="relative"><MagnifyingGlass size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={el ? 'Αναζήτηση…' : 'Search…'} className="h-8 w-40 rounded-none pl-7 text-xs" data-testid="schedule-search" /></div>
        <JobFilterChecks sel={sel} el={el} />
        <div className="ml-auto flex flex-wrap gap-1.5">
          <PdfPrintButtons label="Full Cleaning Schedule" path="/cleaning/schedule/pdf" params={pdfParams('')} testId="schedule-export-full" />
          <PdfPrintButtons label="Individual Cleaner Schedule" path="/cleaning/schedule/pdf" params={pdfParams(cleanerId)} disabled={!cleanerId} testId="schedule-export-individual" />
        </div>
      </div>
      <SelectionBar sel={sel} el={el} />
      <div className="bg-white border border-zinc-200 overflow-x-auto" data-testid="schedule-table-wrap">
        <table className="w-full text-xs border-collapse" data-testid="schedule-table">
          <thead><tr className="bg-zinc-900 text-white text-[10px] uppercase tracking-wider">
            <th className="px-1 py-1.5 w-6" />
            <th className="px-1.5 py-1.5 text-right font-semibold w-8" data-testid="schedule-head-no">No.</th>
            <th className="text-left px-2 py-1.5 font-semibold min-w-[110px] sm:min-w-[160px]">Building / Client</th>
            {weeks.map((w) => <th key={w.index} className="text-left px-2 py-1.5 font-semibold border-l border-zinc-700 whitespace-nowrap min-w-[80px]" data-testid={`schedule-head-week-${w.index}`}>Week {w.index} <span className="text-[9px] font-normal text-zinc-400 normal-case">({pad(w.start)}–{pad(w.end)})</span></th>)}
            <th className="text-left px-2 py-1.5 font-semibold border-l border-zinc-700 whitespace-nowrap min-w-[80px]" data-testid="schedule-head-roof">Roof</th>
            <th className="text-left px-2 py-1.5 font-semibold border-l border-zinc-700 whitespace-nowrap min-w-[80px]" data-testid="schedule-head-windows">Windows</th>
          </tr></thead>
          <tbody>
            {!data && <tr><td colSpan={weeks.length + 5} className="py-10 text-center text-zinc-400">…</td></tr>}
            {data && rows.length === 0 && <tr><td colSpan={weeks.length + 5} className="py-10 text-center text-zinc-400" data-testid="schedule-empty">{el ? 'Δεν υπάρχουν γραμμές για τα επιλεγμένα φίλτρα' : 'No rows for the selected filters'}</td></tr>}
            {rows.map((r, i) => <ScheduleRow key={r.row_key} r={r} no={i + 1} selected={sel.selected.has(r.row_key)} toggle={sel.toggle} weeks={weeks} openAdd={openAdd} />)}
          </tbody>
        </table>
      </div>
      <AddDialog target={addTarget} cleaners={cleaners} el={el} onClose={() => setAddTarget(null)} onSaved={() => { setAddTarget(null); load(); }} />
    </div>
  );
};

export const CleaningSchedule = () => {
  const { language } = useLanguage();
  const el = language === 'el';
  const [view, setView] = useState('schedule');
  const [cleaners, setCleaners] = useState([]);
  const [rowOptions, setRowOptions] = useState([]);
  useEffect(() => {
    axios.get(`${API}/cleaning/cleaners`, authCfg()).then((r) => setCleaners((r.data || []).filter((c) => !c.is_deleted))).catch(() => {});
    const d = new Date();
    axios.get(`${API}/cleaning/schedule?year=${d.getFullYear()}&month=${d.getMonth() + 1}`, authCfg())
      .then((r) => setRowOptions((r.data.rows || []).map((x) => ({ row_key: x.row_key, name: x.name })))).catch(() => {});
  }, []);
  return (
    <div data-testid="cleaning-schedule">
      <div className="flex flex-wrap gap-0 border border-zinc-300 bg-white w-fit mb-3" data-testid="schedule-view-switch">
        {VIEWS.map(([k, label]) => (
          <button key={k} onClick={() => setView(k)} className={`px-3 py-1.5 text-xs font-semibold border-r border-zinc-200 last:border-r-0 transition-colors ${view === k ? 'bg-zinc-900 text-white' : 'text-zinc-600 hover:bg-zinc-50'}`} data-testid={`schedule-view-${k}`}>{label}</button>
        ))}
      </div>
      {view === 'schedule' && <ScheduleView el={el} cleaners={cleaners} />}
      {view === 'cleaner' && <CleaningReports kind="cleaner" el={el} cleaners={cleaners} rowOptions={rowOptions} />}
      {(view === 'roof' || view === 'windows') && <CleaningAnnual key={view} kind={view} el={el} cleaners={cleaners} rowOptions={rowOptions} />}
    </div>
  );
};
